import {
  KnownProjectModuleSettingsDtoName,
  UserDto,
  KnownDeliveryDtoDeliveryType
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { InfiniteQueryUsersListResultDto } from '../../users.queries';
import { initTable } from '../../../../ui/components/pure/list-table/list-table.init';
import { TableComponents } from '../../../../ui/components/pure/list-table/list-table-cells.component';
import { SVGIconComponent } from '../../../../ui/components/pure/icons/types';
import { Colors } from '../../../../ui/theme/colors';
import { projectModuleToIconMap } from '../../../deliveries/maps/project-module-to-icon.map';
import { deliveryTypeToLabelMap } from '../../../deliveries/maps/delivery-type-to-label.map';
import { mapProjectModuleToExternalDeliveryType } from '../../../project-modules/mappings';
import { getUserCapabilities } from '../../logic/user-capabilities.map';

import { useUsersColumnName } from './use-users-column-name.hook';
import {
  pinned,
  useIsUltraWide,
  useTextColumnWidth,
  WRAP_ANYWHERE
} from '../../../../ui/components/pure/list-table/list-table-column-styles';

// First name, last name, group and contact are left unpinned to share the spare width equally,
// rather than one column soaking it all up on wide and ultra-wide screens. Project and external ID
// are pinned variable text, so they grow on ultra-wide screens.
const FIXED_COLUMN_WIDTHS = {
  project: '120px',
  registered: '105px',
  status: '90px',
  capabilities: '160px',
  managed: '80px',
  externalId: '120px'
} as const;

// On ultra-wide screens contact is capped so an email doesn't sit in a sea of whitespace.
const ULTRA_WIDE_CONTACT_WIDTH = '360px';

function getFirstNameDisplay(user: UserDto): string {
  const firstNameValue = user.firstName || '';
  const preferredNameValue = user.preferredName?.trim();
  return preferredNameValue ? `${firstNameValue} (${preferredNameValue})` : firstNameValue;
}

function getLastNameDisplay(user: UserDto): string {
  const lastNameValue = user.lastName || '';
  const maidenNameValue = user.maidenName?.trim();
  return maidenNameValue ? `${lastNameValue} (nee ${maidenNameValue})` : lastNameValue;
}

type CapabilityIcon = {
  Icon: SVGIconComponent;
  color: string;
  tooltip: string;
};

// Names the capability for its hover tooltip, flagging inherited ones so the grey colour is explained.
function getCapabilityTooltip(name: KnownProjectModuleSettingsDtoName, isInherited: boolean): string {
  const deliveryType = mapProjectModuleToExternalDeliveryType(name);
  let label: string;
  if (
    deliveryType === KnownDeliveryDtoDeliveryType.InboundDryCleaning ||
    deliveryType === KnownDeliveryDtoDeliveryType.OutboundDryCleaning
  ) {
    // Hard code dry cleaning label as user's can drop off outbound dry cleaning aswell
    // This is only for this tooltip
    label = 'Dry Cleaning';
  } else {
    label = deliveryType ? deliveryTypeToLabelMap[deliveryType] : name;
  }
  return isInherited ? `${label} (Inherited)` : label;
}

// Combines the user's explicitly set capabilities (black) with the capabilities
// inherited from the project (secondaryGray), matching the User Details page.
// Both sets are read straight from the UserDto, which the list endpoint populates
// with the user's granted and inherited project modules.
function getCapabilityIcons(row: InfiniteQueryUsersListResultDto): CapabilityIcon[] {
  const { explicitModules, implicitModules } = getUserCapabilities(row.user);

  return [
    ...explicitModules.map((name) => ({
      Icon: projectModuleToIconMap[name],
      color: Colors.black,
      tooltip: getCapabilityTooltip(name, false)
    })),
    ...implicitModules.map((name) => ({
      Icon: projectModuleToIconMap[name],
      color: Colors.secondaryGray,
      tooltip: getCapabilityTooltip(name, true)
    }))
  ];
}

export function useUsersTable(projectID: string) {
  const tb = initTable<InfiniteQueryUsersListResultDto, UserDto>();
  tb.styles({
    table: { tableLayout: 'fixed' }
  });
  const columnLabels = useUsersColumnName(projectID);
  const isUltraWide = useIsUltraWide();
  const textWidth = useTextColumnWidth();

  tb.rowStyles({
    td: { height: '64px' }
  });

  tb.column(columnLabels['PROJECT'], TableComponents.TenantLogo, {
    name: ':project.name',
    imageURL: ':project.imageURL'
  }).options({
    isSortable: false,
    minBreakpoint: 'md',
    headerStyles: pinned(textWidth(FIXED_COLUMN_WIDTHS.project, 'logoOrLabel'))
  });

  tb.column('FIRST NAME', TableComponents.BoldText, {
    value: (row) => getFirstNameDisplay(row.user)
  }).options({
    isSortable: true,
    sortId: 'firstName',
    minBreakpoint: 'md',
    tooltip: `user first name`,
    styles: WRAP_ANYWHERE
  });

  tb.column('LAST NAME', TableComponents.BoldText, {
    value: (row) => getLastNameDisplay(row.user)
  }).options({
    isSortable: true,
    sortId: 'lastName',
    minBreakpoint: 'md',
    tooltip: `user last name`,
    styles: WRAP_ANYWHERE
  });

  tb.column(columnLabels['GROUP'], TableComponents.BoldText, {
    value: ':user.groupName'
  }).options({
    isSortable: true,
    sortId: 'groupName',
    minBreakpoint: 'md',
    tooltip: `user's group name`,
    styles: WRAP_ANYWHERE
  });

  tb.column('CONTACT', TableComponents.EmailMobile, {
    email: ':user.email',
    mobile: ':user.mobile'
  }).options({
    isSortable: false,
    minBreakpoint: 'md',
    tooltip: `user's contact information`,
    headerStyles: isUltraWide ? pinned(ULTRA_WIDE_CONTACT_WIDTH) : undefined,
    styles: WRAP_ANYWHERE
  });

  tb.column('REGISTERED', TableComponents.DateOnly, {
    value: ':user.createdTime'
  }).options({
    isSortable: true,
    sortId: 'createdTime',
    tooltip: `user registered date`,
    minBreakpoint: 'md',
    headerStyles: pinned(FIXED_COLUMN_WIDTHS.registered)
  });

  tb.column('STATUS', TableComponents.Text, {
    value: ':user.status'
  }).options({
    isSortable: true,
    sortId: 'status',
    minBreakpoint: 'md',
    tooltip: `user status`,
    headerStyles: pinned(FIXED_COLUMN_WIDTHS.status)
  });

  tb.column('CAPABILITIES', TableComponents.MultipleSVGCell, {
    SVGs: (row) => getCapabilityIcons(row).map((capability) => capability.Icon),
    colorInput: (row) => getCapabilityIcons(row).map((capability) => capability.color),
    tooltips: (row) => getCapabilityIcons(row).map((capability) => capability.tooltip),
    iconSize: () => '24px',
    maxVisibleSVGs: () => Object.keys(projectModuleToIconMap).length
  }).options({
    isSortable: false,
    minBreakpoint: 'md',
    tooltip: `user's capabilities`,
    headerStyles: pinned(FIXED_COLUMN_WIDTHS.capabilities)
  });

  tb.column('MANAGED', TableComponents.ManagedIconCell, {
    value: ':user.managedByAccount.id',
    tooltipText: ':user.managedByAccount.name'
  }).options({
    isSortable: true,
    sortId: 'managed' as any,
    minBreakpoint: 'md',
    tooltip: `managed user`,
    headerStyles: pinned(FIXED_COLUMN_WIDTHS.managed)
  });

  tb.columnGroup('FULL NAME')
    .row(TableComponents.BoldText, {
      value: ':user.displayName'
    })
    .options({
      isSortable: true,
      sortId: 'firstName',
      maxBreakpoint: 'sm',
      tooltip: 'customer full name'
    });

  tb.columnGroup('')
    .row(TableComponents.Text, { value: ':user.status' })
    .row(TableComponents.TenantLogo, {
      name: ':project.name',
      imageURL: ':project.imageURL'
    })
    .options({
      isSortable: false,
      maxBreakpoint: 'sm',
      styles: { width: '150px', textAlign: 'right' }
    });

  tb.column('EXTERNAL ID', TableComponents.Text, {
    value: (row: InfiniteQueryUsersListResultDto) => row.user.externalID ?? ''
  }).options({
    isSortable: false,
    sortId: 'externalID',
    minBreakpoint: 'md',
    tooltip: `external ID`,
    headerStyles: pinned(textWidth(FIXED_COLUMN_WIDTHS.externalId)),
    styles: WRAP_ANYWHERE
  });

  return tb;
}
